import React from 'react';
import { Box, MenuItem, TextField, Stack, Alert } from '@mui/material';
import { IRatingForm } from '../../Interfaces/IRatingForm';
import { ILegalTransactionState } from '../../Interfaces/ILegalTransactionState';

export type TriggerType = 'never' | 'state' | 'phase' | 'requirement';

export interface ITriggerValue {
  idRatingForm: number | null;
  TriggerType: TriggerType;
  idLegalTransactionState: number | null;
  // Instanz-IDs des Vorgangs
  idLegalPhase: number | null;
  idLegalRequirement: number | null;
}

interface IProps {
  value: ITriggerValue;
  onChange: (v: ITriggerValue) => void;
  ratingForms: IRatingForm[];
  legalTransactionStates: ILegalTransactionState[];
  // Konkrete Instanzen aus dem Vorgang
  phases: Array<{ id: number; label: string }>;
  requirements: Array<{ id: number; label: string }>;
  /** Maßnahmen nach Phasen gruppiert — gleiche Darstellung wie bei der
   *  Automatisierung im Vorgangsmanager (Phase als Überschrift, Maßnahmen
   *  eingerückt darunter). Wenn gesetzt, ersetzt es die flache Liste. */
  requirementGroups?: Array<{
    phaseLabel: string;
    requirements: Array<{ id: number; label: string }>;
  }>;
  disabled?: boolean;
  /** Bogen-Auswahl mitrendern. Wenn die Auswahl in einem eigenen Block
   *  darueber steht, hier ausblenden (default: true). */
  showFormSelect?: boolean;
}

export const RatingFormTriggerEditor: React.FC<IProps> = ({
  value,
  onChange,
  ratingForms,
  legalTransactionStates,
  phases,
  requirements,
  requirementGroups,
  disabled,
  showFormSelect = true,
}) => {
  const triggerType = value.TriggerType || 'state';

  const activeRatingForms = ratingForms.filter(
    f => f.IsActive !== false || f.idRatingForm === value.idRatingForm
  );

  return (
    <Box>
      <Stack spacing={2}>
        {showFormSelect && (
          <TextField
            label='Bewertungsbogen'
            size='small'
            select
            fullWidth
            value={value.idRatingForm ?? ''}
            disabled={disabled}
            onChange={e =>
              onChange({
                ...value,
                idRatingForm:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            helperText='Welcher Bewertungsbogen wird für diesen Vorgang verwendet?'
          >
            <MenuItem value=''>– Kein Bogen –</MenuItem>
            {activeRatingForms.map(f => (
              <MenuItem key={f.idRatingForm} value={f.idRatingForm}>
                {f.Title}
                {f.IsDefault ? ' (Standard)' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label='Versand auslösen durch'
          size='small'
          select
          fullWidth
          value={triggerType}
          disabled={disabled || !value.idRatingForm}
          helperText='Legt fest, zu welchem Zeitpunkt der Bewertungsbogen automatisch an die Mandanten versendet wird.'
          onChange={e =>
            onChange({
              ...value,
              TriggerType: e.target.value as TriggerType,
              // beim Wechsel die alten Refs leeren
              idLegalTransactionState:
                e.target.value === 'state'
                  ? value.idLegalTransactionState
                  : null,
              idLegalPhase:
                e.target.value === 'phase' ? value.idLegalPhase : null,
              idLegalRequirement:
                e.target.value === 'requirement'
                  ? value.idLegalRequirement
                  : null,
            })
          }
        >
          <MenuItem value='never'>Nicht automatisch versenden</MenuItem>
          <MenuItem value='state'>
            Sobald der Vorgang einen bestimmten Status erreicht
          </MenuItem>
          <MenuItem value='phase'>
            Sobald eine bestimmte Phase abgeschlossen ist
          </MenuItem>
          <MenuItem value='requirement'>
            Sobald eine bestimmte Maßnahme erledigt ist
          </MenuItem>
        </TextField>

        {triggerType === 'state' && (
          <TextField
            label='Status'
            size='small'
            select
            fullWidth
            value={value.idLegalTransactionState ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalTransactionState:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            helperText='Bei welchem Statuswechsel soll der Bogen versendet werden? (Standard: Abgeschlossen)'
          >
            <MenuItem value=''>–</MenuItem>
            {legalTransactionStates.map(s => (
              <MenuItem
                key={s.idLegalTransactionState}
                value={s.idLegalTransactionState}
              >
                {s.LegalTransactionState}
              </MenuItem>
            ))}
          </TextField>
        )}

        {triggerType === 'phase' && (
          <TextField
            label='Phase'
            size='small'
            select
            fullWidth
            value={value.idLegalPhase ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalPhase:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            helperText='Sobald diese Phase im Vorgang abgeschlossen wird, wird der Bogen ausgelöst.'
          >
            <MenuItem value=''>–</MenuItem>
            {phases.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {triggerType === 'requirement' && (
          <TextField
            label='Maßnahme/Voraussetzung'
            size='small'
            select
            fullWidth
            value={value.idLegalRequirement ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalRequirement:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            helperText='Sobald diese Maßnahme auf „Erledigt" gesetzt wird, wird der Bewertungsbogen versendet.'
          >
            <MenuItem value=''>Keine Maßnahme ausgewählt</MenuItem>
            {requirementGroups
              ? requirementGroups.flatMap(group => [
                  <MenuItem
                    key={`phase-${group.phaseLabel}`}
                    value={`__phase_${group.phaseLabel}`}
                    disabled
                    sx={{ fontWeight: 600, opacity: 1 }}
                  >
                    {group.phaseLabel}
                  </MenuItem>,
                  ...group.requirements.map(r => (
                    <MenuItem key={r.id} value={r.id} sx={{ pl: 4 }}>
                      {r.label}
                    </MenuItem>
                  )),
                ])
              : requirements.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label}
                  </MenuItem>
                ))}
          </TextField>
        )}

        {triggerType !== 'never' && !value.idRatingForm && (
          <Alert severity='info'>
            Bitte zuerst einen Bewertungsbogen auswählen.
          </Alert>
        )}
      </Stack>
    </Box>
  );
};
